import React, { useEffect, useState } from "react";
import { collection, query, orderBy, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { formatPrice, formatDate } from "../utils/format";
import { Truck, MapPin, Clock } from "lucide-react";
import { getEstimatedDeliveryWindow } from "../utils/shipping";
import toast from "react-hot-toast";

const STATUSES = ["confirmed", "processing", "shipped", "delivered", "cancelled"];
const BADGE = { confirmed: "badge-info", processing: "badge-warning", shipped: "badge-default", delivered: "badge-success", cancelled: "badge-danger" };

export default function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  async function load() {
    const snap = await getDocs(query(collection(db, "orders"), orderBy("createdAt", "desc")));
    setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function updateStatus(id, status) {
    await updateDoc(doc(db, "orders", id), { status });
    toast.success(`Order marked as ${status}`);
    load();
    if (selected?.id === id) setSelected(prev => ({ ...prev, status }));
  }

  const filtered = filter === "all" ? orders : orders.filter(o => o.status === filter);

  return (
    <div style={{ padding: "40px 48px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>Orders</h1>
          <p style={{ color: "var(--text-muted)", fontSize: 15, marginTop: 4 }}>Manage and fulfill customer orders.</p>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap", background: "#fff", padding: 8, borderRadius: 12, border: "1px solid var(--border)", width: "fit-content" }}>
        {["all", ...STATUSES].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: filter === s ? "var(--brand)" : "transparent", color: filter === s ? "#fff" : "var(--text-muted)", fontSize: 14, fontWeight: filter === s ? 600 : 500, cursor: "pointer", textTransform: "capitalize", transition: "all 0.2s" }}
            onMouseEnter={e => { if (filter !== s) e.currentTarget.style.color = "var(--text)"; }}
            onMouseLeave={e => { if (filter !== s) e.currentTarget.style.color = "var(--text-muted)"; }}>
            {s === "all" ? "All Orders" : s}
          </button>
        ))}
      </div>

      <div className={selected ? "admin-detail-layout" : ""} style={{ display: "grid", gridTemplateColumns: selected ? undefined : "1fr", gap: 24, alignItems: "start", transition: "all 0.3s ease" }}>
        {/* Table */}
        <div className="card" style={{ overflow: "hidden", background: "#fff", border: "1px solid var(--border)" }}>
          {loading ? <div style={{ padding: 60, textAlign: "center" }}><div className="loader"><div className="spinner" /></div></div> : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}>
                    {["Order", "Customer", "Fulfillment", "Date", "Items", "Total", "Status", ""].map(h => (
                      <th key={h} style={{ padding: "16px 20px", textAlign: "left", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(order => (
                    <tr key={order.id} onClick={() => setSelected(order)}
                      style={{ borderBottom: "1px solid var(--border)", cursor: "pointer", background: selected?.id === order.id ? "rgba(232, 71, 26, 0.04)" : "", transition: "background 0.2s" }}
                      onMouseEnter={e => { if (selected?.id !== order.id) e.currentTarget.style.background = "#F8FAFC"; }}
                      onMouseLeave={e => { if (selected?.id !== order.id) e.currentTarget.style.background = ""; }}>
                      <td style={{ padding: "16px 20px", fontWeight: 600, fontFamily: "monospace", fontSize: 14 }}>#{order.id.slice(0, 8).toUpperCase()}</td>
                      <td style={{ padding: "16px 20px" }}>
                        <div style={{ fontWeight: 600, color: "var(--text)" }}>{order.userName || "—"}</div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{order.userEmail}</div>
                      </td>
                      <td style={{ padding: "16px 20px" }}>
                        <span
                          className="badge"
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            padding: "4px 8px",
                            borderRadius: 6,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            background: order.fulfillmentType === "pickup" ? "rgba(16, 185, 129, 0.1)" : "rgba(59, 130, 246, 0.1)",
                            color: order.fulfillmentType === "pickup" ? "#059669" : "#2563EB",
                          }}
                        >
                          {order.fulfillmentType === "pickup" ? (
                            <><MapPin size={11} /> Pickup ({order.delivery?.state || "Station"})</>
                          ) : (
                            <><Truck size={11} /> Delivery ({order.delivery?.state || "State"})</>
                          )}
                        </span>
                      </td>
                      <td style={{ padding: "16px 20px", fontSize: 13, color: "var(--text-muted)" }}>{formatDate(order.createdAt)}</td>
                      <td style={{ padding: "16px 20px", fontWeight: 500 }}>{order.items?.length || 0}</td>
                      <td style={{ padding: "16px 20px", fontWeight: 700, color: "var(--brand)" }}>{formatPrice(order.total)}</td>
                      <td style={{ padding: "16px 20px" }}>
                        <span className={`badge ${BADGE[order.status] || "badge-default"}`} style={{ textTransform: "capitalize", padding: "6px 12px", fontSize: 12, fontWeight: 700 }}>{order.status}</span>
                      </td>
                      <td style={{ padding: "16px 20px", color: "var(--brand)", fontSize: 14, fontWeight: 600 }}>{selected?.id === order.id ? "Viewing" : "View →"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <p style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>No orders found.</p>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="card" style={{ padding: 28, position: "sticky", top: 24, background: "#fff", border: "1px solid var(--border)", boxShadow: "var(--shadow-md)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid var(--border)" }}>
              <div>
                <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 18 }}>Order #{selected.id.slice(0, 8).toUpperCase()}</h3>
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <span className={`badge ${BADGE[selected.status] || "badge-default"}`} style={{ textTransform: "capitalize", padding: "4px 8px", fontSize: 11, fontWeight: 700 }}>{selected.status}</span>
                  <span
                    className="badge"
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      background: selected.fulfillmentType === "pickup" ? "rgba(16, 185, 129, 0.1)" : "rgba(59, 130, 246, 0.1)",
                      color: selected.fulfillmentType === "pickup" ? "#059669" : "#2563EB",
                    }}
                  >
                    {selected.fulfillmentType === "pickup" ? "Station Pickup" : "Door Delivery"}
                  </span>
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ border: "none", background: "var(--surface)", width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 18, color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#FEE2E2"; e.currentTarget.style.color = "#EF4444"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.color = "var(--text-muted)"; }}>×</button>
            </div>

            {/* ⏱️ 7-Day Fulfillment Deadline */}
            <div style={{ padding: "10px 14px", background: "rgba(232, 71, 26, 0.05)", border: "1px solid rgba(232, 71, 26, 0.15)", borderRadius: 8, marginBottom: 18, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
              <Clock size={16} color="var(--brand)" />
              <div>
                <span style={{ fontWeight: 700 }}>7-Day Delivery Deadline: </span>
                <span style={{ color: "var(--brand)", fontWeight: 600 }}>
                  {selected.estimatedDeliveryDate || getEstimatedDeliveryWindow(selected.createdAt, 7).dateText}
                </span>
              </div>
            </div>

            <div style={{ marginBottom: 18 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Customer</p>
              <div style={{ padding: 12, background: "#F8FAFC", borderRadius: 8 }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{selected.userName}</p>
                <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>{selected.userEmail}</p>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {selected.fulfillmentType === "pickup" ? "Pickup Hub Information" : "Delivery Address"}
              </p>
              <div style={{ padding: 12, background: "#F8FAFC", borderRadius: 8 }}>
                {selected.fulfillmentType === "pickup" ? (
                  <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                    <p style={{ margin: "0 0 2px", fontWeight: 700, color: "var(--brand)" }}>
                      🏪 {selected.delivery?.pickupLocationName || selected.pickupLocation?.name || "Afriport Pickup Hub"}
                    </p>
                    <p style={{ margin: 0, color: "var(--text)" }}>
                      {selected.delivery?.pickupAddress || selected.pickupLocation?.address},{" "}
                      {selected.delivery?.pickupCity || selected.pickupLocation?.city}, {selected.delivery?.state}
                    </p>
                    {(selected.delivery?.pickupPhone || selected.pickupLocation?.phone) && (
                      <p style={{ margin: "2px 0 0", color: "var(--text-muted)" }}>
                        Hub Tel: {selected.delivery?.pickupPhone || selected.pickupLocation?.phone}
                      </p>
                    )}
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px dashed var(--border)", fontSize: 12, color: "var(--text-muted)" }}>
                      Collector: <strong>{selected.delivery?.fullName || selected.userName}</strong> ({selected.delivery?.phone || "No phone"})
                    </div>
                  </div>
                ) : (
                  <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--text)", margin: 0 }}>
                    <span style={{ fontWeight: 600 }}>{selected.delivery?.fullName}</span> · {selected.delivery?.phone}<br />
                    {selected.delivery?.address}<br />
                    {selected.delivery?.city}, {selected.delivery?.state} State
                  </p>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 28 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>Items</p>
              {selected.items?.map(item => (
                <div key={item.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                  <span><span style={{ fontWeight: 600, color: "var(--brand)", marginRight: 8 }}>{item.qty}x</span> {item.name}</span>
                  <span style={{ fontWeight: 600, color: "var(--text)" }}>{formatPrice(item.price * item.qty)}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, marginTop: 16, fontSize: 18, color: "var(--text)" }}>
                <span>Total</span>
                <span>{formatPrice(selected.total)}</span>
              </div>
            </div>

            <div>
              <p style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>Update Status</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {STATUSES.map(s => (
                  <button key={s} onClick={() => updateStatus(selected.id, s)}
                    style={{ padding: "10px 14px", border: `1px solid ${selected.status === s ? "var(--brand)" : "var(--border)"}`, borderRadius: 8, background: selected.status === s ? "var(--brand)" : "#fff", color: selected.status === s ? "#fff" : "var(--text-muted)", fontSize: 13, fontWeight: selected.status === s ? 600 : 500, cursor: "pointer", textTransform: "capitalize", transition: "all 0.2s" }}
                    onMouseEnter={e => { if (selected.status !== s) { e.currentTarget.style.borderColor = "#CBD5E1"; e.currentTarget.style.color = "var(--text)"; } }}
                    onMouseLeave={e => { if (selected.status !== s) { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text-muted)"; } }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
